// Dates in the pace timezone (Europe/Helsinki) and their display.
import type { UiLang } from '@valyrio/engine/types';
import { addDays, localDate, startOfLocalDay } from './engine';

export const DEFAULT_TZ = 'Europe/Helsinki';

export function todayLocal(now: Date, tz = DEFAULT_TZ): string {
  return localDate(now, tz);
}

/** The instant the next local day starts. */
export function startOfNextDay(now: Date, tz = DEFAULT_TZ): Date {
  return startOfLocalDay(addDays(localDate(now, tz), 1), tz);
}

const FI_WD = ['su', 'ma', 'ti', 'ke', 'to', 'pe', 'la'];
const EN_WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const EN_MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "su 11.10." (fi) / "Sun 11 Oct" (en) for a local date "YYYY-MM-DD". */
export function shortDate(date: string, lang: UiLang): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return lang === 'fi' ? `${FI_WD[wd]} ${d}.${m}.` : `${EN_WD[wd]} ${d} ${EN_MON[m - 1]}`;
}

/** "11.10." (fi) / "11 Oct" (en): a local date without its weekday, where room is short (the week road). */
export function dayMonth(date: string, lang: UiLang): string {
  const [, m, d] = date.split('-').map(Number) as [number, number, number];
  return lang === 'fi' ? `${d}.${m}.` : `${d} ${EN_MON[m - 1]}`;
}

/** "12.10.2026" / "12 Oct 2026" for an ISO instant, in the pace timezone. */
export function longDate(iso: string, lang: UiLang, tz = DEFAULT_TZ): string {
  const date = localDate(new Date(iso), tz);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return lang === 'fi' ? `${d}.${m}.${y}` : `${d} ${EN_MON[m - 1]} ${y}`;
}

/** Interval until a due date, as on the grade buttons: "10 min", "6 pv", "2 kk", "1,2 v". */
export function formatInterval(ms: number, lang: UiLang): string {
  const min = Math.max(1, Math.round(ms / 60_000));
  const fmt = (n: number) => (lang === 'fi' ? String(n).replace('.', ',') : String(n));
  const round1 = (n: number) => Math.round(n * 10) / 10;
  if (min < 60) return `${min} min`;
  const h = min / 60;
  if (h < 24) return lang === 'fi' ? `${Math.round(h)} t` : `${Math.round(h)} h`;
  const d = h / 24;
  if (d < 30.5) return lang === 'fi' ? `${Math.round(d)} pv` : `${Math.round(d)} d`;
  const mo = d / 30.44;
  if (mo < 12) return lang === 'fi' ? `${fmt(round1(mo))} kk` : `${fmt(round1(mo))} mo`;
  return lang === 'fi' ? `${fmt(round1(d / 365.25))} v` : `${fmt(round1(d / 365.25))} y`;
}
